import React, { useState } from "react";
import { TextField, MenuItem, Box } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enAU } from "date-fns/locale";

import {
	subDays,
	startOfWeek,
	subWeeks,
	endOfWeek,
	startOfMonth,
	endOfMonth,
	subMonths,
} from "date-fns";

import { useAppDispatch, useAppSelector } from "../../../store/hooks.ts";
import {
	selectReportType,
	setReportStartDate,
	setReportEndDate,
	selectReportStartDate,
	selectReportEndDate,
	setReportType,
} from "../../../store/slices/reportsSlice.ts";

const ReportsDatePicker = () => {
	const dispatch = useAppDispatch();
	const reportType = useAppSelector(selectReportType);
	const startDateString = useAppSelector(selectReportStartDate);
	const endDateString = useAppSelector(selectReportEndDate);

	const [presetValue, setPresetValue] = useState("last90Days");

	const startDate = new Date(startDateString);
	const endDate = new Date(endDateString);

	const handleStartDateChange = (date: Date | null) => {
		if (date) {
			const newStart = new Date(date);
			newStart.setHours(0, 0, 0, 0);
			dispatch(setReportStartDate(newStart.toISOString()));
		}
	};

	const handleEndDateChange = (date: Date | null) => {
		if (date) {
			const newEnd = new Date(date);
			newEnd.setHours(23, 59, 59, 999);
			dispatch(setReportEndDate(newEnd.toISOString()));
		}
	};

	const handlePresetChange = (event) => {
		const value = event.target.value;
		setPresetValue(value);

		let start,
			end = new Date();

		switch (value) {
			case "last7Days":
				start = subDays(end, 6);
				break;
			case "last30Days":
				start = subDays(end, 29);
				break;
			case "last60Days":
				start = subDays(end, 59);
				break;
			case "last90Days":
				start = subDays(end, 89);
				break;
			case "pastFullCalendarWeek":
				if (new Date().getDay() === 0) {
					start = startOfWeek(subWeeks(new Date(), 1), {
						weekStartsOn: 2,
					});
					end = new Date();
				} else {
					start = startOfWeek(new Date(), { weekStartsOn: 2 });
					start = subWeeks(start, 1);
					end = endOfWeek(start, { weekStartsOn: 1 });
				}
				break;
			case "pastFullCalendarMonth":
				if (end.getDate() !== endOfMonth(end).getDate()) {
					end = endOfMonth(subMonths(end, 1));
				}
				start = startOfMonth(end);
				start.setDate(start.getDate() + 1);
				break;
			case "pastFullCalendarYear":
				const lastYear = new Date().getFullYear() - 1;
				start = new Date(lastYear, 0, 2); // 1 Jan of last year
				end = new Date(lastYear, 11, 31, 23, 59, 59, 999); // 31 Dec of last year
				break;
			default:
				start = subMonths(end, 12);
		}

		dispatch(setReportStartDate(start.toISOString().split("T")[0]));
		dispatch(setReportEndDate(end.toISOString().split("T")[0]));
	};

	const handleReportTypeChange = (event) => {
		dispatch(setReportType(event.target.value));
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					gap: 2,
					alignItems: "center",
					width: "100%",
				}}
			>
				<TextField
					select
					label="Report Type"
					size="small"
					value={reportType || ""}
					onChange={handleReportTypeChange}
					sx={{ flex: 2 }}
				>
					<MenuItem value="ageAndGender">Age and Sex</MenuItem>
					<MenuItem value="monthlyIncome">Monthly Income</MenuItem>
					<MenuItem value="speciality">Speciality</MenuItem>
					<MenuItem value="averagePerSession">
						Average Per Session
					</MenuItem>
				</TextField>
				<TextField
					select
					label="Preset"
					size="small"
					value={presetValue}
					onChange={handlePresetChange}
					sx={{ flex: 2 }}
				>
					<MenuItem value="last7Days">Last 7 Days</MenuItem>
					<MenuItem value="last30Days">Last 30 Days</MenuItem>
					<MenuItem value="last60Days">Last 60 Days</MenuItem>
					<MenuItem value="last90Days">Last 90 Days</MenuItem>
					<MenuItem value="pastFullCalendarWeek">
						Past Calendar Week
					</MenuItem>
					<MenuItem value="pastFullCalendarMonth">
						Past Calendar Month
					</MenuItem>
					<MenuItem value="pastFullCalendarYear">
						Past Calendar Year
					</MenuItem>
				</TextField>

				<DesktopDatePicker
					label="From"
					inputFormat="dd/MM/yyyy"
					maxDate={endDate}
					value={startDate}
					onChange={handleStartDateChange}
					slotProps={{ textField: { size: "small" } }}
					sx={{ flex: 1 }}
				/>

				<DesktopDatePicker
					label="To"
					inputFormat="dd/MM/yyyy"
					minDate={startDate}
					value={endDate}
					onChange={handleEndDateChange}
					slotProps={{ textField: { size: "small" } }}
					sx={{ flex: 1 }}
				/>
			</Box>
		</LocalizationProvider>
	);
};

export default ReportsDatePicker;
